/* eslint-disable no-underscore-dangle */
import React, { useMemo, useState, useEffect } from 'react'

import { useSelector } from 'react-redux'
import ReactTable from 'react-table'
import moment from 'moment'
import { Link } from 'react-router-dom'
import axios from 'axios'

import modifyInventoryIcon from '../../../assets/storage-events/modify-inventory.png'
import orderIcon from '../../../assets/storage-events/order.png'
import supplierOrderIcon from '../../../assets/storage-events/supplier-order.png'
import addItemIcon from '../../../assets/storage-events/add-item.png'
import calendarIcon from '../../../assets/storage-events/calendar.png'
import removeItemIcon from '../../../assets/storage-events/remove-item.png'
import { dateFromObjectId } from '../../../common/utils'
import useSWR from '../../../hooks/useSWR'

const pawnIcon = 'https://s3-eu-west-1.amazonaws.com/qlocx-bilder/public/pawn.png'

const eventIconsMapping = {
  storageOrder: orderIcon,
  QuantityChange: modifyInventoryIcon,
  MaxQuantityChange: modifyInventoryIcon,
  MinQuantityChange: modifyInventoryIcon,
  storageSupplierOrder: supplierOrderIcon,
  NewItem: addItemIcon,
  DeleteItem: removeItemIcon,
  PreferredDeliveryDaysChange: calendarIcon,
  doorOpen: pawnIcon,
}

const eventsWithDetails = ['storageOrder', 'QuantityChange', 'MinQuantityChange', 'MaxQuantityChange', 'storageSupplierOrder', 'NewItem', 'DeleteItem', 'PreferredDeliveryDaysChange']

const getCustomerName = (t, customer, company) => (customer ? `${customer.name} ${customer.surname}` : `${t('Unknown user')} (${company.name || t('Unknown company')})`)

const getLockInfo = (t, lock, shouldShowLockInfo) => {
  if (!lock) return ''

  return shouldShowLockInfo ? `, (${lock.custom_name}) ${t('at')} ${lock.address.street}` : ''
}

const LockEventItem = ({ t, locale, event, lock, shouldShowLockInfo, search }) => {
  const customer = useSelector((state) => state.collectionData.keyedCustomers && state.collectionData.keyedCustomers[event.customer || event.clientId])
  const lockUser = customer && lock && lock.users && lock.users.find((u) => u.customer === customer._id)
  const company = useSelector((state) => lockUser && state.collectionData.keyedCompanies && state.collectionData.keyedCompanies[lockUser.company]) || {}

  const [isShowingEventDetails, setIsShowingEventDetails] = useState(false)
  const [pendingOrderUpdate, setPendingOrderUpdate] = useState()

  const { data: eventDetails } = useSWR(isShowingEventDetails && `subject/admin/events/lock/${lock._id}/event/${event._id}?eventType=${event.type}`)

  useEffect(() => {
    if (eventDetails && event.type === 'storageOrder') {
      setPendingOrderUpdate(eventDetails.data)
    }
  }, [eventDetails])

  const description = useMemo(() => {
    const renderStorageOrder = (orderId) => (
      <>
        {t('{customerName} created a withdrawal with withdrawal ID', { customerName: getCustomerName(t, customer, company) })}
        &nbsp;
        <Link to={{ pathname: `/storage/${lock?.storage?._id}`, search: '?iframeRoute=storageOrders' }} target="_blank">
          <span className="link">{orderId}</span>
        </Link>
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderCustomerQuantityChange = (item) => (
      <>
        {t('{customerName} updated the balance for {itemName}', {
          customerName: getCustomerName(t, customer, company),
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderCustomerMinQuantityChange = (item) => (
      <>
        {t('{customerName} updated the order point for {itemName}', {
          customerName: getCustomerName(t, customer, company),
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderCustomerMaxQuantityChange = (item) => (
      <>
        {t('{customerName} updated the max balance for {itemName}', {
          customerName: getCustomerName(t, customer, company),
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderSupplierOrderCreatorQuantityChange = (itemsCount) => (
      <>
        {t('Qlocx performed an automatic update of the balance on {itemsCount} items', { itemsCount })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderAdminQuantityChange = (item) => (
      <>
        {t('{adminName} updated the balance for {itemName}', {
          adminName: event.admin?.email || '-',
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderAdminMinQuantityChange = (item) => (
      <>
        {t('{adminName} updated the order point for {itemName}', {
          adminName: event.admin?.email || '-',
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderAdminMaxQuantityChange = (item) => (
      <>
        {t('{adminName} updated the max balance for {itemName}', {
          adminName: event.admin?.email || '-',
          itemName: item.name,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderStorageSupplierOrder = () => {
      const eventDescription = event.admin ? t('{email} performed a manual order of articles according to order', { email: event.admin?.email || '-' }) : t('Qlocx performed an automatic order of articles according to order')

      return (
        <>
          {eventDescription} &nbsp;
          <Link to={{ pathname: `/storage/${lock?.storage?._id}`, search: '?iframeRoute=storageSupplierOrders' }} target="_blank">
            <span className="link">{event._id}</span>
          </Link>
          {getLockInfo(t, lock, shouldShowLockInfo)}
        </>
      )
    }

    const renderDoorOpen = () => (
      <>
        {t('{customerName} opened {doorType}', {
          customerName: getCustomerName(t, customer, company),
          doorType: lock._id === event.lock ? t('the door') : t('the carrier door'),
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
        {event.request_action === 'remoteAccess' && ` (${t('remote access')})`}
      </>
    )

    const renderCarrierDoorOpen = () => (
      <>
        {t('Driver from {carrierName} opened {doorType}', {
          carrierName: event.carrier.organization,
          doorType: lock._id === event.lock ? t('the door') : t('the carrier door'),
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderNewItem = () => (
      <>
        {t('{customerName} added {itemsCount} items', {
          customerName: event.clientType === 'customer' ? getCustomerName(t, customer, company) : event.admin?.email || '-',
          itemsCount: event.itemsCount,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderDeleteItem = () => (
      <>
        {t('{customerName} deleted {itemsCount} items', {
          customerName: event.clientType === 'customer' ? getCustomerName(t, customer, company) : event.admin?.email || '-',
          itemsCount: event.itemsCount,
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    const renderPreferredDeliveryDaysChange = () => (
      <>
        {t('{adminName} changed the preferred delivery days', {
          adminName: event.admin?.email || '-',
        })}
        {getLockInfo(t, lock, shouldShowLockInfo)}
      </>
    )

    // info: "lock" will never be a carrier door, even if event.lock is a carrier door
    switch (event.type) {
      case 'storageOrder':
        return renderStorageOrder(event.orderId)
      case 'QuantityChange':
        // eslint-disable-next-line no-nested-ternary
        return event.clientType === 'customer' ? renderCustomerQuantityChange(event.item) : event.clientType === 'supplierOrderCreator' ? renderSupplierOrderCreatorQuantityChange(event.itemsCount) : renderAdminQuantityChange(event.item)
      case 'MaxQuantityChange':
        return event.clientType === 'customer' ? renderCustomerMaxQuantityChange(event.item) : renderAdminMaxQuantityChange(event.item)
      case 'MinQuantityChange':
        return event.clientType === 'customer' ? renderCustomerMinQuantityChange(event.item) : renderAdminMinQuantityChange(event.item)
      case 'storageSupplierOrder':
        return renderStorageSupplierOrder()
      case 'doorOpen':
        return renderDoorOpen()
      case 'carrierDoorOpen':
        return renderCarrierDoorOpen()
      case 'NewItem':
        return renderNewItem()
      case 'DeleteItem':
        return renderDeleteItem()
      case 'PreferredDeliveryDaysChange':
        return renderPreferredDeliveryDaysChange()
      default:
        return null
    }
  }, [event, customer, company, lock, lockUser, t])

  if (search) {
    const compareString = `${customer ? customer.name : ''} ${customer ? customer.surname : ''} ${company ? company.name : ''} ${event.carrier ? event.carrier.organization : ''}`

    if (!compareString.toLowerCase().includes(search.toLowerCase())) return null
  }

  const handleBlur = () => {
    const orderRows = pendingOrderUpdate.map((row) => ({ ...row, mark: row.mark || row.item.mark }))

    axios.put(`${window.MYQLOCX_API}/subject/admin/storage_orders/${event._id}`, orderRows).catch((err) => {
      console.log(err)
    })
  }

  const handleChange = (e, row) => {
    const newPendingOrderUpdate = pendingOrderUpdate.map((item) => {
      if (item.item.articleNumber === row._original.item.articleNumber) {
        return {
          ...item,
          item: {
            ...item.item,
            mark: e.target.value,
          },
        }
      }
      return item
    })

    setPendingOrderUpdate(newPendingOrderUpdate)

    setTimeout(() => {
      const input = document.getElementById(`${row._original.item.articleNumber}-${event.orderId}`)
      input.focus()
    }, 0)
  }

  const renderEditable = (row) => <input id={`${row._original.item.articleNumber}-${event.orderId}`} style={{ backgroundColor: 'white', width: '80%', border: 'none' }} value={row._original.item.mark} onChange={(e) => handleChange(e, row)} onBlur={() => handleBlur(row)} />

  const renderEventDetails = () => {
    if (!isShowingEventDetails) return null

    if (!eventDetails?.data) {
      return <div className="flex justify-content-center">{t('Loading...')}</div>
    }

    const translatedColumns = eventDetails.columns.map((c) => {
      if (c.accessor === 'item.mark') {
        return {
          ...c,
          Header: t(c.Header),
          Cell: ({ row }) => renderEditable(row),
        }
      }

      if (c.accessor === 'prevDeliveryDays' || c.accessor === 'deliveryDays') {
        return {
          Header: t(c.Header),
          Cell: ({ row }) => {
            const value = row._original[c.accessor].map((day) => new Date(2024, 0, day).toLocaleString(locale, { weekday: 'long' })).join(', ')

            return value
          },
        }
      }

      return {
        ...c,
        Header: t(c.Header),
      }
    })

    const data = event.type === 'storageOrder' ? pendingOrderUpdate : eventDetails.data

    if (!data) {
      return <div className="flex justify-content-center">{t('Loading...')}</div>
    }

    return (
      <div style={{ padding: 20 }}>
        <ReactTable style={{ textAlign: 'center' }} data={data} columns={translatedColumns} pageSize={data.length} showPagination={false} />
      </div>
    )
  }

  return (
    <div style={{ background: '#FFF', borderRadius: 10 }}>
      <div style={{ display: 'flex', marginBottom: 5, borderRadius: 10, padding: 15, boxShadow: 'grey 1px 1px 5px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img src={eventIconsMapping[event.type] || (event.carrier && event.carrier.image)} style={{ height: 30, width: 30 }} />
        </div>
        <div style={{ flexGrow: 1, padding: 15 }}>{description}</div>
        {eventsWithDetails.includes(event.type) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 100 }}>
            <div className="sub-title-text-container" style={{ padding: 10, marginTop: 0, marginBottom: 0 }} onClick={() => setIsShowingEventDetails(!isShowingEventDetails)}>
              {isShowingEventDetails ? t('Show less') : t('Show more')}
            </div>
          </div>
        )}
        <div style={{ textAlign: 'right', padding: 10, minWidth: 150, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>{moment(dateFromObjectId(event._id)).format('Do MMM YY [-] HH:mm')}</div>
      </div>
      {renderEventDetails()}
    </div>
  )
}

export default LockEventItem
